import * as React from "react";

import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import AiSummary from "./components/AiSummary";
import BuyerInfo from "./components/BuyerInfo";
import CpvCodes from "./components/CpvCodes";
import { DecisionMakerContacts } from "./components/DecisionMakerContacts";
import FloatingSignup from "./components/FloatingSignup";
import LargeCallToAction from "./components/LargeCallToAction";
import SimilarTenders from "./components/SimilarTenders";
import CallToAction from "./components/SmallCallToAction";
import StatusIcon from "./components/StatusIcon";
import TopBuyers from "./components/TopBuyers";
import ViewedNoticeCounter from "./components/ViewedNoticeCounter";
import Breadcrumbs from "./Breadcrumbs";
import { TenderStage } from "./landing_types";
import { SEO_URLS } from "./landing_utils";
import { Link } from "./LandingPageLinks";
import { SimilarRecord } from "./RecordCardShelf";
import { Footer, Header } from "./shared";

import css from "./RecordLandingPage.module.scss";

export type CpvCode = {
  title: string;
  code: string;
  href?: string;
};

export const SIGNUP_LINK = (id: string) =>
  `https://app.stotles.com/get-started?utm_source=seo_lp&dataType=record&id=${id}`;

type Props = {
  app_root_url: string;
  similar_records?: SimilarRecord[];
  related_topic_pages?: CpvCode[];
  weighted_buyers?: Link[];
  record: {
    name: string;
    description?: string;
    guid: string;
    buyers: {
      id: string;
      guid: string;
      name: string;
      contact?: { name?: string; email?: string };
      link?: string;
    }[];
    suppliers?: { name: string; guid: string }[];
    value?: number;
    currency?: string;
    publish_date: string;
    close_date?: string;
    stage: TenderStage;
    cpv_codes: CpvCode[];
    url: string;
    award_date?: string;
    expiry_date?: string;
  };
  related_contacts?: { jobTitle: string; seniority: string }[];
};

function RecordLandingPage({
  record,
  app_root_url,
  similar_records,
  related_topic_pages,
  weighted_buyers,
  related_contacts,
}: Props) {
  // TODO: handle multiple buyers
  const buyer = record.buyers[0];

  const breadcrumbTenderType = (() => {
    switch (record.stage) {
      case TenderStage.AWARDED:
        return { id: "awards", text: "Awards" };
      case TenderStage.PRE_TENDER:
        return { id: "pre-tenders", text: "Pre-Tenders" };
      case TenderStage.OPEN:
        return { id: "tenders", text: "Tenders" };
      default:
        return { id: "tenders", text: "Tenders" };
    }
  })();

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        id: "home",
        name: "Home",
        href: "/",
      },
      {
        id: breadcrumbTenderType.id,
        name: breadcrumbTenderType.text,
        href: SEO_URLS.tenderSearch,
      },
      {
        id: buyer.guid,
        name: buyer.name,
        href: buyer.link,
      },
      {
        id: record.guid,
        name: record.name,
      },
    ];
  }, [record, buyer, breadcrumbTenderType]);

  const orderedTopicPages = React.useMemo(() => {
    return record.cpv_codes.sort((a, b) =>
      // sort by "has link" then title
      (a.href && b.href) || (!a.href && !b.href) ? a.title.localeCompare(b.title) : a.href ? -1 : 1,
    );
  }, [record.cpv_codes]);

  const LARGE_CTA_HEADER = "Sign up to the Stotles Tender Tracker for free ";
  const LARGE_CTA_TEXT =
    "Find even more contracts with advanced search capability and AI powered relevance scoring.";

  const convertTenderStageToStatus = (stage: TenderStage) => {
    switch (stage) {
      case TenderStage.AWARDED:
        return "awarded";
      case TenderStage.PRE_TENDER:
        return "preTender";
      case TenderStage.OPEN:
        return "openTender";
      case TenderStage.CLOSED:
        return "awarded";
      case TenderStage.STALE_PRE_TENDER:
        return undefined;
      default:
        return undefined;
    }
  };

  // Generate a random number between 2 and 100 that is persistent for each record
  const randomNumber = (parseInt(record.guid.replace(/[^0-9]/g, ""), 10) % 99) + 2;

  const combinedTopicPages = React.useMemo(() => {
    return [...(orderedTopicPages || []), ...(related_topic_pages || [])].flat();
  }, [orderedTopicPages, related_topic_pages]);

  return (
    <div className={css.landingPage}>
      <Header appRootUrl={app_root_url} dataType="record" id={record.guid} />
      <main className={css.pageLayout}>
        <div className={css.section}>
          <Breadcrumbs items={breadcrumbs} className={css.breadcrumbs} />
          <div className={css.aboveTheFold}>
            <div className={css.infoContainer}>
              <div className={css.statusAndPublishDateContainer}>
                <StatusIcon status={convertTenderStageToStatus(record.stage)} />
                <p className={css.publishDate}>
                  Published{" "}
                  <time title={record.publish_date} dateTime={record.publish_date}>
                    {toHumanRelativeDateFromNow(record.publish_date)}
                  </time>
                </p>
              </div>
              <h1 className={css.title}>{record.name}</h1>
              <ViewedNoticeCounter suppliersCount={randomNumber} />
              <CallToAction
                content={"Track & Win Public Sector Contracts and Tenders. Sign up for Free"}
                buttonLink={SIGNUP_LINK(record.guid)}
                ctaContent={"Track Tenders"}
              />
            </div>
            <div className={css.buyerInfoContainer}>
              <BuyerInfo
                name={buyer?.name}
                buyerLink={buyer?.link}
                value={record?.value}
                currency={record?.currency}
                supplier={record?.suppliers?.[0]?.name}
                expiryDate={record?.expiry_date}
                closeDate={record?.close_date}
              />
            </div>
          </div>
        </div>

        <div className={css.section}>
          <div className={css.belowTheFold}>
            <div className={css.belowTheFoldContent}>
              <div className={css.description}>
                <h2 className={css.descriptionTitle}>Description</h2>
                <p>{record.description}</p>
              </div>
              <AiSummary />
              <div>
                {related_contacts && related_contacts.length > 0 && (
                  <DecisionMakerContacts
                    contacts={related_contacts}
                    buttonLink={SIGNUP_LINK(record.guid)}
                  />
                )}
                <CallToAction
                  content={"Unlock decision maker contacts."}
                  ctaContent={"Sign up for free"}
                  buttonLink={SIGNUP_LINK(record.guid)}
                />
              </div>
            </div>
            <div className={css.floatingSignupContainer}>
              <FloatingSignup buttonLink={SIGNUP_LINK(record.guid)} />
            </div>
          </div>
        </div>
        {similar_records && similar_records.length > 0 && (
          <div className={css.section}>
            <SimilarTenders similar_records={similar_records} />
          </div>
        )}
        {weighted_buyers && weighted_buyers.length > 0 && (
          <div className={css.section}>
            <TopBuyers weighted_buyers={weighted_buyers} />
          </div>
        )}
        {combinedTopicPages && combinedTopicPages.length > 0 && (
          <div className={css.section}>
            <h2 className={css.descriptionTitle}>CPV Codes</h2>
            <CpvCodes items={combinedTopicPages} />
          </div>
        )}
        <div className={css.section}>
          <LargeCallToAction
            header={LARGE_CTA_HEADER}
            content={LARGE_CTA_TEXT}
            buttonLink={SIGNUP_LINK(record.guid)}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default RecordLandingPage;
